import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Typography, LinearProgress, Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHouse } from "@fortawesome/free-solid-svg-icons";
import "./RCPlus.scss";
import { isSubThresholdOutputWatt } from "../../utils/telemetry";

const API_BASE = "https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod";
const POLL_INTERVAL = 30_000; // 30 seconds

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------
const formatValue = (value, fractionDigits = 1) => {
  const numericValue = parseFloat(value);
  if (Number.isNaN(numericValue)) {
    return "--";
  }
  if (numericValue > 0 && numericValue < 0.1) {
    return "<0.1";
  }
  return numericValue.toFixed(fractionDigits);
};

const TWO_DECIMAL_KEYS = new Set([
  "output_volt",
  "output_freq",
  "output_current",
  "output_va",
  "output_watt",
]);

const formatMetricValue = (key, rawValue) => {
  const decimals = TWO_DECIMAL_KEYS.has(key) ? 2 : 1;
  return formatValue(rawValue, decimals);
};

const withUnit = (formattedValue, unit) =>
  formattedValue === "--" ? `--${unit}` : `${formattedValue}${unit}`;

const fmtMetricUnit = (key, value, unit) => withUnit(formatMetricValue(key, value), unit);

const fmtScreen2W = (value) => {
  const numericValue = parseFloat(value);
  if (Number.isNaN(numericValue)) {
    return "--W";
  }
  if (isSubThresholdOutputWatt(numericValue)) {
    return "<12W";
  }
  return withUnit(formatMetricValue("output_watt", value), "W");
};

const fmtScreen2VA = (value) => {
  const numericValue = parseFloat(value);
  if (Number.isNaN(numericValue)) {
    return "--VA";
  }
  if (numericValue >= 0 && numericValue < 12) {
    return "<12VA";
  }
  return withUnit(formatMetricValue("output_va", value), "VA");
};

const fmtScreen4W = (value) => {
  const numericValue = parseFloat(value);
  if (Number.isNaN(numericValue)) {
    return "--W";
  }
  if (numericValue >= 0 && numericValue < 12) {
    return "<12W";
  }
  return withUnit(formatValue(value, 2), "W");
};

const fmtScreen4VA = (value) => {
  const numericValue = parseFloat(value);
  if (Number.isNaN(numericValue)) {
    return "--VA";
  }
  if (numericValue >= 0 && numericValue < 12) {
    return "<12VA";
  }
  return withUnit(formatMetricValue("input_va", value), "VA");
};

const fmtPF   = (w, va) => {
  const nw = parseFloat(w);
  const nva = parseFloat(va);
  if (isNaN(nw) || isNaN(nva) || nva === 0) return "0.000";
  return Math.min(1, Math.abs(nw / nva)).toFixed(3);
};
const NETLINK_VERSION = "0.10";

const fmtVersion = (value) => {
  const numericValue = parseFloat(value);
  if (Number.isNaN(numericValue)) {
    return "--.--";
  }
  return numericValue.toFixed(2);
};

const parseUtcDbDateTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) {
    return null;
  }

  const normalizedDate = String(dateStr).trim().replace(/-/g, "/");
  const [year, month, day] = normalizedDate.split("/").map((part) => Number(part));

  const rawTime = String(timeStr).trim().split(".")[0];
  const [hoursRaw, minutesRaw, secondsRaw] = rawTime.split(":");
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  const seconds = secondsRaw == null ? 0 : Number(secondsRaw);

  if (
    [year, month, day, hours, minutes, seconds].some((value) => Number.isNaN(value))
  ) {
    return null;
  }

  return new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));
};

const formatLocalDateTime = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "----/--/-- --:--:--";
  }

  const pad = (value) => String(value).padStart(2, "0");
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
};

const fmtDateTime = (dateStr, timeStr) => {
  const localDate = parseUtcDbDateTime(dateStr, timeStr);
  return formatLocalDateTime(localDate);
};

const formatRelativeAge = (seconds) => {
  const numericSeconds = Number(seconds);
  if (!Number.isFinite(numericSeconds)) {
    return "N/A";
  }

  if (numericSeconds < 60) {
    return `${Math.max(0, Math.round(numericSeconds))}s ago`;
  }

  const minutes = Math.floor(numericSeconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes ? `${hours}h ${remainingMinutes}m ago` : `${hours}h ago`;
};

const formatStatusText = (value) => {
  if (!value) {
    return "N/A";
  }

  return String(value)
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatUploadStateText = (value) => {
  if (String(value || "").toLowerCase() === "recovered") {
    return "Online";
  }

  return formatStatusText(value);
};

const formatLastModbusState = (status) => {
  const normalizedStatus = String(status || "").toLowerCase();

  if (normalizedStatus === "ok" || normalizedStatus === "healthy") {
    return "OK";
  }

  if (normalizedStatus === "modbus_failed") {
    return "Failed";
  }

  if (normalizedStatus === "modbus_degraded" || normalizedStatus === "degraded") {
    return "Degraded";
  }

  if (
    normalizedStatus === "health_unavailable" ||
    normalizedStatus === "not_communicating" ||
    normalizedStatus === "unknown" ||
    normalizedStatus === "offline"
  ) {
    return "N/A";
  }

  return formatStatusText(status);
};

const getHealthPresentationReason = (healthStatus) => {
  const rawReason = healthStatus?.reason || "No health data";
  const currentOnlyReason = String(rawReason)
    .replace(/;?\s*upload recovered after .*?offline/gi, "")
    .trim();

  if (currentOnlyReason) {
    return currentOnlyReason;
  }

  if (healthStatus?.currentStatus === "healthy" || healthStatus?.currentStatus === "ok") {
    return "Device reporting normally";
  }

  return "No health data";
};

const getHealthDisplay = (healthStatus, isLoading, healthError) => {
  if (isLoading && !healthStatus) {
    return {
      label: "Loading",
      shortLabel: "Health --",
      severity: "gray",
      reason: "Checking health",
    };
  }

  if (healthError) {
    return {
      label: "Unavailable",
      shortLabel: "Health N/A",
      severity: "gray",
      reason: healthError,
    };
  }

  const severity = healthStatus?.severity || "gray";
  const currentStatus = healthStatus?.currentStatus || healthStatus?.status || "unknown";
  const labels = {
    healthy: "Healthy",
    ok: "Healthy",
    degraded: "Degraded",
    modbus_degraded: "Degraded",
    modbus_failed: "Modbus Fault",
    offline: "Offline",
    health_unavailable: "Health Unavailable",
    not_communicating: "Gateway Offline",
    unknown: "Unknown",
  };

  const shortLabels = {
    healthy: "Health OK",
    ok: "Health OK",
    degraded: "Health DEG",
    modbus_degraded: "Health DEG",
    modbus_failed: "Health FAIL",
    offline: "Health OFF",
    health_unavailable: "Health N/A",
    not_communicating: "Gateway OFF",
    unknown: "Health --",
  };

  return {
    label: labels[currentStatus] || "Unknown",
    shortLabel: shortLabels[currentStatus] || "Health --",
    severity,
    reason: getHealthPresentationReason(healthStatus),
  };
};

const MODE_LABELS = { 0: "Standby", 1: "Inverting", 2: "Charging", 3: "Pwr Save" };
const getStatusLabel = (mode, errorCode, record = null) => {
  if (errorCode && parseInt(errorCode) !== 0) return "Fault";

  const dbModeText =
    record?.mode_text ??
    record?.mode_name ??
    record?.operating_mode ??
    record?.mode_desc ??
    null;

  if (dbModeText && String(dbModeText).trim()) {
    return String(dbModeText).trim();
  }

  return MODE_LABELS[parseInt(mode)] ?? "Unknown";
};

const deriveModelCode = (productType) => {
  const raw = String(productType ?? "EVO").toUpperCase();
  const match = raw.match(/[A-Z]{2,4}-\d{4}/);
  if (match) return match[0];
  return raw.substring(0, 8);
};

const fmtScreen1Volt = (v) => {
  const formatted = withUnit(formatMetricValue("output_volt", v), "V");
  return formatted.padStart(7, " ");
};

const fmtScreen1Amp = (v) => {
  const formatted = withUnit(formatMetricValue("output_current", v), "A");
  return formatted.padStart(7, " ");
};

const fmtScreen1Hz = (v) => {
  const formatted = withUnit(formatMetricValue("output_freq", v), "Hz");
  return formatted.padStart(7, " ");
};

const fmtFixedUnit = (value, decimals, unit) => {
  const numericValue = parseFloat(value);
  if (Number.isNaN(numericValue)) {
    return `--${unit}`;
  }
  return `${numericValue.toFixed(decimals)}${unit}`;
};

const fmtBatteryVolt = (value) => fmtFixedUnit(value, 2, "V");
const fmtBatteryAmp = (value) => fmtFixedUnit(value, 1, "A");

const mergeTelemetryRecord = (record, previousRecord = null) => {
  if (!record) {
    return previousRecord;
  }

  return {
    ...(previousRecord || {}),
    ...record,
    battery_temp: record.battery_temp ?? previousRecord?.battery_temp ?? null,
    heatsink_temp: record.heatsink_temp ?? previousRecord?.heatsink_temp ?? null,
    transformer_temp: record.transformer_temp ?? previousRecord?.transformer_temp ?? null,
  };
};

// ---------------------------------------------------------------------------
// LCD row builder — pads label with spaces so label + value = exactly 20 chars.
// This mirrors the physical 20-column fixed-character LCD grid.
// ---------------------------------------------------------------------------
const COLS = 20;
const row = (label, value, isHeader = false) => {
  const v = String(value ?? "");
  const split = Math.max(0, COLS - v.length);
  const l = String(label ?? "").padEnd(split, " ").slice(0, split);
  const text = `${l}${v}`.padEnd(COLS, " ").slice(0, COLS);
  return { label: l, value: v, text, split, isHeader };
};

const headerRow = (model, mode) => {
  const modelPart = String(model ?? "").padEnd(8, " ").slice(0, 8);
  const modeText = String(mode ?? "").slice(0, 12);
  const modePart = modeText.padStart(12, " ");
  return {
    label: modelPart,
    value: modePart,
    text: `${modelPart}${modePart}`,
    split: 8,
    isHeader: true,
  };
};

const centeredRow = (text, leftMargin = 0, rightMargin = 0) => {
  const raw = String(text ?? "");
  const innerWidth = Math.max(0, COLS - leftMargin - rightMargin);
  const trimmed = raw.length > innerWidth ? raw.slice(0, innerWidth) : raw;
  const totalPad = innerWidth - trimmed.length;
  const leftPad = Math.floor(totalPad / 2);
  const rightPad = totalPad - leftPad;
  const content = `${" ".repeat(leftMargin + leftPad)}${trimmed}${" ".repeat(rightPad + rightMargin)}`
    .padEnd(COLS, " ")
    .slice(0, COLS);

  return {
    label: content,
    value: "",
    text: content,
    split: COLS,
    isHeader: false,
  };
};

const fmtDateTimeCompact = (dateStr, timeStr) => {
  const full = fmtDateTime(dateStr, timeStr);
  if (full === "----/--/-- --:--:--") {
    return full;
  }
  return full.slice(2);
};

// ---------------------------------------------------------------------------
// Screen definitions — each screen is always exactly 4 rows × 20 columns
// ---------------------------------------------------------------------------
function buildScreens(data, fwVersion, productType, lineMode = 4) {
  const model = deriveModelCode(productType);
  const status = data ? getStatusLabel(data.mode, data.error_code, data) : "--";
  const row0 = headerRow(model, status);

  const makeScreen = (index, visibleRows, targetRows) => {
    const rows = [row0, ...visibleRows];
    while (rows.length < targetRows) {
      rows.push(row("", ""));
    }
    return { index, rows: rows.slice(0, targetRows) };
  };

  if (!data) {
    const targetRows = lineMode === 8 ? 8 : 4;
    const pageCount = lineMode === 8 ? 4 : 7;
    return Array.from({ length: pageCount }, (_, index) =>
      makeScreen(index, [row(index === 0 ? "Loading..." : "", "")], targetRows)
    );
  }

  const fourLineScreens = [
    makeScreen(0, [
      row("AC output:", fmtScreen1Volt(data.output_volt)),
      row("", fmtScreen1Amp(data.output_current)),
      row("", fmtScreen1Hz(data.output_freq)),
    ], 4),
    makeScreen(1, [
      row("AC output:", fmtScreen2W(data.output_watt)),
      row("", fmtScreen2VA(data.output_va)),
      centeredRow(`PF = ${fmtPF(data.output_watt, data.output_va)}`),
    ], 4),
    makeScreen(2, [
      row("AC input:", fmtMetricUnit("gen_volt", data.gen_volt, "V")),
      row("", fmtMetricUnit("input_current", data.input_current, "A")),
      row("", fmtMetricUnit("gen_freq", data.gen_freq, "Hz")),
    ], 4),
    makeScreen(3, [
      row("AC input:", fmtScreen4W(data.input_watt)),
      row("", fmtScreen4VA(data.input_va)),
      centeredRow(`PF = ${fmtPF(data.input_watt, data.input_va)}`),
    ], 4),
    makeScreen(4, [
      row("Battery:", fmtBatteryVolt(data.battery_volt)),
      row("", fmtBatteryAmp(data.battery_current)),
      row("External:", fmtBatteryAmp(data.external_current)),
    ], 4),
    makeScreen(5, [
      row("Battery:", fmtMetricUnit("battery_temp", data.battery_temp, "\u00b0C")),
      row("Heat sink:", fmtMetricUnit("heatsink_temp", data.heatsink_temp, "\u00b0C")),
      row("Transformer:", fmtMetricUnit("transformer_temp", data.transformer_temp, "\u00b0C")),
    ], 4),
    makeScreen(6, [
      row("EVO Ver:", fmtVersion(fwVersion)),
      row("NetLink Ver:", NETLINK_VERSION),
      centeredRow(fmtDateTimeCompact(data.date, data.time), 1, 1),
    ], 4),
  ];

  if (lineMode === 4) {
    return fourLineScreens;
  }

  return [
    makeScreen(0, [
      row("AC output:", fmtScreen1Volt(data.output_volt)),
      row("", fmtScreen1Amp(data.output_current)),
      row("", fmtScreen1Hz(data.output_freq)),
      row("", ""),
      row("", fmtScreen2W(data.output_watt)),
      row("", fmtScreen2VA(data.output_va)),
      centeredRow(`PF = ${fmtPF(data.output_watt, data.output_va)}`),
    ], 8),
    makeScreen(1, [
      row("AC input:", fmtMetricUnit("gen_volt", data.gen_volt, "V")),
      row("", fmtMetricUnit("input_current", data.input_current, "A")),
      row("", fmtMetricUnit("gen_freq", data.gen_freq, "Hz")),
      row("", ""),
      row("", fmtScreen4W(data.input_watt)),
      row("", fmtScreen4VA(data.input_va)),
      centeredRow(`PF = ${fmtPF(data.input_watt, data.input_va)}`),
    ], 8),
    makeScreen(2, [
      row("Battery:", fmtBatteryVolt(data.battery_volt)),
      row("", fmtBatteryAmp(data.battery_current)),
      row("External:", fmtBatteryAmp(data.external_current)),
      row("", ""),
      row("Battery:", fmtMetricUnit("battery_temp", data.battery_temp, "\u00b0C")),
      row("Heat sink:", fmtMetricUnit("heatsink_temp", data.heatsink_temp, "\u00b0C")),
      row("Transformer:", fmtMetricUnit("transformer_temp", data.transformer_temp, "\u00b0C")),
    ], 8),
    makeScreen(3, [
      row("EVO Ver:", fmtVersion(fwVersion)),
      row("NetLink Ver:", NETLINK_VERSION),
      row("", ""),
      row("", ""),
      row("", ""),
      centeredRow(fmtDateTimeCompact(data.date, data.time), 1, 1),
      row("", ""),
    ], 8),
  ];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function RCPlus() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // ---- resolve product from nav state or sessionStorage ----
  const getProduct = () => {
    if (location.state?.product) return location.state.product;
    try {
      const cached = sessionStorage.getItem(`dashboardProduct:${id}`);
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  };

  const [product] = useState(() => {
    const p = getProduct();
    if (p) sessionStorage.setItem(`dashboardProduct:${id}`, JSON.stringify(p));
    return p;
  });

  const [liveData, setLiveData]     = useState(null);
  const [fwVersion, setFwVersion]   = useState(null);
  const [healthStatus, setHealthStatus] = useState(null);
  const [isHealthLoading, setIsHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState("");
  const [lineMode, setLineMode]     = useState(4);
  const [currentScreen, setScreen]  = useState(0);
  const [nowTs, setNowTs] = useState(() => Date.now());
  const [processingStatus, setProcessingStatus] = useState("\u00A0");
  const [, setTelemetryFreshness] = useState(null);
  const [progress, setProgress] = useState(0);
  const [error, setError]           = useState(null);

  const lastLogIdRef = useRef(null);
  const logQueueRef = useRef([]);
  const fetchIntervalRef = useRef(null);
  const processingFileRef = useRef(null);
  const telemetryCursorRef = useRef(null);
  const productId = product?.id || id;

  const ESTIMATED_SAMPLE_RATE = 8;
  const TARGET_BUFFER_SECONDS = 30;
  const FAST_FORWARD_THRESHOLD_SECONDS = 90;
  const MAX_BUFFER_SECONDS = 150;
  const MAX_DEQUEUE_PER_TICK = 5;
  const CURRENT_WINDOW_SECONDS = 300;
  const DATA_BUFFER_SIZE = ESTIMATED_SAMPLE_RATE * MAX_BUFFER_SECONDS;
  const TARGET_BUFFER_SAMPLES = ESTIMATED_SAMPLE_RATE * TARGET_BUFFER_SECONDS;
  const FAST_FORWARD_THRESHOLD_SAMPLES =
    ESTIMATED_SAMPLE_RATE * FAST_FORWARD_THRESHOLD_SECONDS;

  const formatProcessingLabel = (name, percent) => `Processing ${name} (${percent}%)`;

  const clearProcessingStatus = useCallback(() => {
    processingFileRef.current = null;
    setProcessingStatus("\u00A0");
  }, []);

  const markSamplesProcessed = useCallback((sampleLogId, count = 1) => {
    const info = processingFileRef.current;
    if (!info || sampleLogId !== info.id) {
      return;
    }

    info.processed = Math.min(info.total, info.processed + count);

    if (info.processed >= info.total) {
      clearProcessingStatus();
      setProgress(100);
      return;
    }

    const percent = info.total > 0 ? Math.round((info.processed / info.total) * 100) : 0;
    setProcessingStatus(formatProcessingLabel(info.name, percent));
    setProgress(percent);
  }, [clearProcessingStatus]);

  const getBufferedSeconds = useCallback(
    () => logQueueRef.current.length / ESTIMATED_SAMPLE_RATE,
    []
  );

  const updateProgressFromBacklog = useCallback(() => {
    if (processingFileRef.current) {
      return;
    }

    const backlogSeconds = getBufferedSeconds();

    if (backlogSeconds <= 0) {
      setProgress(100);
      return;
    }

    if (backlogSeconds >= FAST_FORWARD_THRESHOLD_SECONDS) {
      setProgress(0);
      return;
    }

    const normalized =
      ((FAST_FORWARD_THRESHOLD_SECONDS - backlogSeconds) / FAST_FORWARD_THRESHOLD_SECONDS) * 100;
    setProgress(Math.max(0, Math.min(normalized, 100)));
  }, [getBufferedSeconds]);

  const maybeFastForwardQueue = useCallback(() => {
    if (logQueueRef.current.length <= FAST_FORWARD_THRESHOLD_SAMPLES) {
      return;
    }

    const samplesToKeep = Math.max(TARGET_BUFFER_SAMPLES, ESTIMATED_SAMPLE_RATE * 10);
    if (logQueueRef.current.length <= samplesToKeep) {
      return;
    }

    const samplesToDrop = logQueueRef.current.length - samplesToKeep;
    const droppedEntries = logQueueRef.current.slice(0, samplesToDrop);

    if (processingFileRef.current && samplesToDrop > 0) {
      const droppedForCurrent = droppedEntries.filter(
        (entry) => entry.log_id === processingFileRef.current.id
      ).length;
      if (droppedForCurrent > 0) {
        markSamplesProcessed(processingFileRef.current.id, droppedForCurrent);
      }
    }

    logQueueRef.current = logQueueRef.current.slice(-samplesToKeep);
    updateProgressFromBacklog();
  }, [markSamplesProcessed, updateProgressFromBacklog]);

  const updateTelemetryCursor = useCallback((sample) => {
    if (!sample) {
      return;
    }

    const nextCursor = sample.cursor || {
      sampleId: sample.id,
      logId: sample.log_id,
      sampleTimestamp: sample.sampleTimestamp,
    };
    const nextSampleId = Number(nextCursor.sampleId);
    const currentSampleId = Number(telemetryCursorRef.current?.sampleId);

    if (!Number.isFinite(nextSampleId)) {
      return;
    }

    if (!Number.isFinite(currentSampleId) || nextSampleId >= currentSampleId) {
      telemetryCursorRef.current = nextCursor;
    }
  }, []);

  const processNextDataPoint = useCallback(() => {
    if (!logQueueRef.current.length) {
      updateProgressFromBacklog();
      return;
    }

    const currentData = logQueueRef.current.shift();
    if (!currentData) {
      return;
    }

    setLiveData((previousData) => mergeTelemetryRecord(currentData, previousData));
    updateTelemetryCursor(currentData);
    setError(null);

    markSamplesProcessed(currentData.log_id);
    updateProgressFromBacklog();
  }, [markSamplesProcessed, updateProgressFromBacklog, updateTelemetryCursor]);

  const getTelemetryStatusText = useCallback((freshness) => {
    if (!freshness) {
      return "\u00A0";
    }

    if (freshness.freshnessStatus === "stale") {
      return `Stale telemetry: ${formatRelativeAge(freshness.ageSeconds)}`;
    }

    if (freshness.freshnessStatus === "empty") {
      return freshness.freshnessReason || "No telemetry samples found";
    }

    return "\u00A0";
  }, []);

  // ---- data fetch ----
  const fetchData = useCallback(async () => {
    if (!productId) {
      return;
    }

    try {
      const cursor = telemetryCursorRef.current;
      const url = new URL(`${API_BASE}/logs/get/current/${productId}`);
      url.searchParams.set("windowSeconds", CURRENT_WINDOW_SECONDS);
      url.searchParams.set("maxRows", 120);

      if (cursor?.sampleId) {
        url.searchParams.set("afterSampleId", cursor.sampleId);
        url.searchParams.set("fallback", "false");
      }

      const res = await fetch(url.toString());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      if (body?.status !== "ok") {
        throw new Error(body?.error || "Current telemetry unavailable");
      }

      const currentState = body.data || {};
      const records = Array.isArray(currentState.recentSamples)
        ? currentState.recentSamples
        : [];

      setTelemetryFreshness(currentState);
      setProcessingStatus(getTelemetryStatusText(currentState));

      if (!cursor?.sampleId && currentState.latestSample) {
        const initialRecords = records.length ? records : [currentState.latestSample];
        const latestRecord = initialRecords[initialRecords.length - 1];

        setLiveData((previousData) =>
          initialRecords.reduce(
            (mergedData, record) => mergeTelemetryRecord(record, mergedData),
            previousData
          )
        );
        updateTelemetryCursor(latestRecord);
        lastLogIdRef.current = latestRecord.log_id || latestRecord.cursor?.logId || null;
        setProgress(100);
        setError(null);
        return;
      }

      if (records.length === 0) {
        updateProgressFromBacklog();
        return;
      }

      const latestRecord = records[records.length - 1];
      const latestLogId = latestRecord?.log_id;
      updateTelemetryCursor(latestRecord);
      lastLogIdRef.current = latestLogId ?? lastLogIdRef.current;

      logQueueRef.current = [...logQueueRef.current, ...records];
      if (logQueueRef.current.length > DATA_BUFFER_SIZE) {
        logQueueRef.current = logQueueRef.current.slice(-DATA_BUFFER_SIZE);
      }

      maybeFastForwardQueue();
      updateProgressFromBacklog();
    } catch (e) {
      setProcessingStatus("Error fetching data");
      setProgress(0);
      setError("Unable to fetch data");
      console.error("[RCPlus] fetch error:", e);
    }
  }, [
    getTelemetryStatusText,
    productId,
    DATA_BUFFER_SIZE,
    maybeFastForwardQueue,
    updateProgressFromBacklog,
    updateTelemetryCursor,
  ]);

  const fetchFirmware = useCallback(async () => {
    if (!productId) {
      setFwVersion(null);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/logs/get/version/${productId}`);
      const body = await res.json();

      if (res.ok && body?.data?.firmwareVersion !== undefined) {
        const numericValue = Number(body.data.firmwareVersion);
        setFwVersion(Number.isNaN(numericValue) ? null : (numericValue / 100).toFixed(2));
        return;
      }

      setFwVersion(null);
    } catch {
      setFwVersion(null);
    }
  }, [productId]);

  const fetchHealth = useCallback(async () => {
    if (!productId) {
      setHealthStatus(null);
      setHealthError("");
      setIsHealthLoading(false);
      return;
    }

    setIsHealthLoading(true);
    try {
      const res = await fetch(`${API_BASE}/logs/get/health/${productId}`);
      const body = await res.json();

      if (res.ok && body?.status === "ok") {
        setHealthStatus(body.data || null);
        setHealthError("");
        return;
      }

      setHealthStatus(null);
      setHealthError("Health unavailable");
    } catch (healthFetchError) {
      setHealthStatus(null);
      setHealthError("Health unavailable");
      console.error("[RCPlus] health fetch error:", healthFetchError);
    } finally {
      setIsHealthLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    const timerId = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(timerId);
  }, []);

  useEffect(() => {
    telemetryCursorRef.current = null;
    lastLogIdRef.current = null;
    logQueueRef.current = [];
    processingFileRef.current = null;
    setLiveData(null);
    setTelemetryFreshness(null);
    setProcessingStatus("\u00A0");
    setProgress(0);
    setError(null);
  }, [productId]);

  useEffect(() => {
    if (!productId) {
      return undefined;
    }

    fetchData();
    fetchFirmware();
    fetchHealth();

    fetchIntervalRef.current = setInterval(() => {
      fetchData();
      fetchHealth();
    }, POLL_INTERVAL);
    return () => {
      if (fetchIntervalRef.current) {
        clearInterval(fetchIntervalRef.current);
      }
    };
  }, [productId, fetchData, fetchFirmware, fetchHealth]);

  useEffect(() => {
    const updateInterval = setInterval(() => {
      if (!logQueueRef.current.length) {
        updateProgressFromBacklog();
        return;
      }

      const backlogSamples = logQueueRef.current.length;
      const needsCatchUp = backlogSamples > TARGET_BUFFER_SAMPLES;
      const dequeueTarget = needsCatchUp
        ? Math.min(
            MAX_DEQUEUE_PER_TICK,
            Math.max(2, Math.ceil(backlogSamples / TARGET_BUFFER_SAMPLES))
          )
        : 1;

      for (let i = 0; i < dequeueTarget; i += 1) {
        if (!logQueueRef.current.length) {
          break;
        }
        processNextDataPoint();
      }
    }, 1000);

    return () => clearInterval(updateInterval);
  }, [processNextDataPoint, updateProgressFromBacklog]);

  // ---- build screen content ----
  const screens = buildScreens(liveData, fwVersion, product?.product_type, lineMode);
  const totalScreens = screens.length;
  const safeScreenIndex = Math.min(Math.max(currentScreen, 0), Math.max(totalScreens - 1, 0));
  const screen = screens[safeScreenIndex] ?? { rows: [] };

  useEffect(() => {
    setScreen(0);
  }, [lineMode]);

  useEffect(() => {
    if (currentScreen >= totalScreens) {
      setScreen(Math.max(totalScreens - 1, 0));
    }
  }, [currentScreen, totalScreens]);

  // ---- navigation handlers ----
  const handleUp   = () => setScreen((s) => (s - 1 + totalScreens) % totalScreens);
  const handleDown = () => setScreen((s) => (s + 1) % totalScreens);
  const handleToggleLineMode = () => {
    setLineMode((mode) => (mode === 4 ? 8 : 4));
  };

  const getLastUpdateText = () => {
    if (liveData?.date && liveData?.time) {
      const sampleDate = parseUtcDbDateTime(liveData.date, liveData.time);
      if (!sampleDate) {
        return "N/A";
      }
      const elapsedMillis = Math.max(0, nowTs - sampleDate.getTime());
      const elapsedSeconds = Math.round(elapsedMillis / 1000);
      const elapsedMinutes = Math.floor(elapsedSeconds / 60);
      const minuteDisplay = `approx ${elapsedMinutes + 1}min ago`;
      return `${formatLocalDateTime(sampleDate)} (${minuteDisplay})`;
    }
    return "N/A";
  };

  const healthDisplay = getHealthDisplay(healthStatus, isHealthLoading, healthError);
  const healthTooltipRows =
    healthStatus?.currentStatus === "not_communicating"
      ? [
          {
            label: "Gateway uploads",
            value: "None found",
          },
          {
            label: "Next step",
            value: "Check connectivity",
          },
        ]
      : healthStatus?.healthAvailable === false
      ? [
          {
            label: "Health telemetry",
            value: "Not available",
          },
        ]
      : [
          {
            label: "Last report",
            value: healthStatus?.uploadTimestamp
              ? formatRelativeAge(healthStatus.ageSeconds)
              : "N/A",
          },
          {
            label: "Report time",
            value: healthStatus?.uploadTimestamp
              ? formatLocalDateTime(new Date(healthStatus.uploadTimestamp))
              : "N/A",
          },
          {
            label: "Last Modbus",
            value: formatLastModbusState(
              healthStatus?.reportedStatus || healthStatus?.status || healthStatus?.currentStatus
            ),
          },
          {
            label: "Upload",
            value: formatUploadStateText(healthStatus?.uploadState),
          },
        ];

  if (
    healthStatus?.healthAvailable !== false &&
    healthStatus?.uploadState === "upload_failed" &&
    Number(healthStatus?.uploadOfflineSeconds) > 0
  ) {
    healthTooltipRows.push({
      label: "Outage",
      value: formatRelativeAge(healthStatus.uploadOfflineSeconds).replace(" ago", ""),
    });
  }

  const healthDetails = (
    <div className="rcplusHealthTooltipContent">
      <div className="rcplusHealthTooltipTitle">{healthDisplay.label}</div>
      <div className="rcplusHealthTooltipReason">{healthDisplay.reason}</div>
      {healthTooltipRows.map((row) => (
        <div className="rcplusHealthTooltipRow" key={row.label}>
          <span>{row.label}</span>
          <strong>{row.value}</strong>
        </div>
      ))}
    </div>
  );

  return (
    <div className="rcplusPage">
      <div className="rcplusPageNav">
        <button className="navigationButton rcplusHomeButton" type="button" onClick={() => navigate("/product")}>
          <FontAwesomeIcon icon={faHouse} />
          My Products
        </button>
      </div>

      <div className="rcplusDevice">

        {/* ---- Top-left logo ---- */}
        <div className="rcplusBrand">
          <img className="rcplusBrandLogo" src="/Samlex_Power_logo_RGB.png" alt="Samlex Power logo" />
        </div>

        <Tooltip
          title={healthDetails}
          arrow
          placement="top"
          enterTouchDelay={0}
          leaveTouchDelay={5000}
          slotProps={{
            tooltip: { className: "rcplusHealthMuiTooltip" },
            arrow: { className: "rcplusHealthMuiTooltipArrow" },
          }}
        >
          <div
            className={`rcplusHealthChip rcplusHealthChip--${healthDisplay.severity}`}
            role="button"
            tabIndex={0}
            aria-label={`Device health: ${healthDisplay.label}. ${healthDisplay.reason}`}
          >
            <span className="rcplusHealthLed" aria-hidden="true" />
            <span className="rcplusHealthText">{healthDisplay.shortLabel}</span>
          </div>
        </Tooltip>

        {/* ---- LCD bezel + display ---- */}
        <div className="rcplusLcdBezel">
          <div className={`rcplusLcd ${lineMode === 4 ? "rcplusLcd--4line" : ""}`}>
            {screen.rows.map((row, i) => (
              <div key={i} className={`lcdRow${row.isHeader ? " lcdRow--header" : ""}`}>
                {row.text.split("").map((ch, cellIndex) => {
                  const className = row.isHeader
                    ? "lcdCell lcdCell--header"
                    : cellIndex < row.split
                      ? "lcdCell lcdCell--label"
                      : "lcdCell lcdCell--value";

                  return (
                    <span key={`${i}-${cellIndex}`} className={className}>
                      {ch === " " ? "\u00A0" : ch}
                    </span>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Screen indicator dots */}
          <div className="lcdDots">
            {screens.map((_, i) => (
              <button
                key={i}
                className={`lcdDot ${i === safeScreenIndex ? "lcdDot--active" : ""}`}
                onClick={() => setScreen(i)}
                aria-label={`Screen ${i + 1}`}
              />
            ))}
          </div>

          {/* ---- Physical-style buttons ---- */}
          <div className="rcplusBtnRow">
            <button
              className="rcplusBtn rcplusLineModeBtn"
              onClick={handleToggleLineMode}
              title={`Switch to ${lineMode === 4 ? "8" : "4"} line display`}
            >
              <span className="rcplusBtnLabel">{lineMode === 4 ? "4L" : "8L"}</span>
            </button>
            <div className="rcplusNavBtnGroup">
              <button className="rcplusBtn rcplusNavBtn" onClick={handleUp} title="Previous screen">
                <span className="rcplusBtnIcon">&#9650;</span>
                <span className="rcplusBtnLabel">Up</span>
              </button>
              <button className="rcplusBtn rcplusNavBtn" onClick={handleDown} title="Next screen">
                <span className="rcplusBtnIcon">&#9660;</span>
                <span className="rcplusBtnLabel">Down</span>
              </button>
            </div>
          </div>
        </div>

        {/* ---- Bottom-right model family label ---- */}
        <div className="rcplusNetlinkLabel">EVO-NetLink</div>

      </div>

      {/* ---- Dashboard-style footer below device ---- */}
      <div className="rcplusFooter" aria-live="polite">
        <div className="rcplusFooterTop">
          <Typography variant="body2" component="p">
            {error ? "Error fetching data" : processingStatus || "\u00A0"}
          </Typography>
          <Typography variant="body2" component="p">
            Last update: {getLastUpdateText()}
          </Typography>
        </div>
        <LinearProgress
          className="rcplusFooterProgress"
          variant="determinate"
          value={progress}
          aria-label="Log replay progress"
        />
      </div>
    </div>
  );
}
